import { useEffect } from 'react';

import { Alert, Anchor, Badge, Button, Group, Loader, Paper, Text } from '@mantine/core';
import { IconExternalLink, IconFileTypeDoc, IconRefresh, IconSparkles } from '@tabler/icons-react';

import { useBlendedReport } from '@/hooks/useBlendedReport';
import { useGoogleDocsExport } from '@/hooks/useGoogleDocsExport';

interface GoogleDocsExportCardProps {
  /** The subject whose blend is exported. Blends are stored per subject, so a
   * report covering several subjects has one export each. */
  readonly subjectId: string;
  /** Shown only when the report covers more than one subject. */
  readonly subjectName?: string;
}

/**
 * The Google Docs export for a GDM escalation's blended report.
 *
 * Deliberately shows the export and nothing else — the blended content itself
 * is what the page around this is already displaying, so repeating it here
 * would be noise.
 *
 * Mounting only LOOKS UP the export (and follows one still running). When
 * none exists — the background exporter died mid-wait, or the blend finished
 * late — the card offers to generate it; opening a report must not write.
 *
 * Admin-only — gate at the call site.
 */
export function GoogleDocsExportCard({
  subjectId,
  subjectName,
}: Readonly<GoogleDocsExportCardProps>) {
  const { fetchBlend, blendedReportId, state: blendState } = useBlendedReport();
  const {
    state,
    googleDocUrl,
    documentTitle,
    error,
    triggerExport,
    retryExport,
    exportId,
    isLoading: exportLoading,
  } = useGoogleDocsExport(blendedReportId);

  // fetchBlend is stable (its own deps bottom out in useCallback(…, [])), so
  // this runs once per subject rather than on every render.
  useEffect(() => {
    fetchBlend(subjectId);
  }, [subjectId, fetchBlend]);

  // Render nothing until a blend is actually found. The caller only mounts
  // this for escalated subjects, but an escalation whose blend has not run
  // yet would otherwise flash an empty card with a spinner on every visit.
  if (!blendedReportId) {
    return null;
  }

  const busy = exportLoading || blendState === 'fetching' || blendState === 'polling';

  return (
    <Paper withBorder p="sm" radius="md" data-testid="google-docs-export-card">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <IconFileTypeDoc size={18} />
          <Text size="sm" fw={500} style={{ whiteSpace: 'nowrap' }}>
            Google Docs export
          </Text>
          {subjectName && (
            <Text size="sm" c="dimmed" lineClamp={1}>
              {subjectName}
            </Text>
          )}
        </Group>

        <Group gap="xs" wrap="nowrap">
          {busy && <Loader size="xs" />}
          <ExportBadge state={state} />
          {state === 'idle' && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconSparkles size={14} />}
              onClick={() => triggerExport()}
              data-testid="google-docs-generate"
            >
              Generate
            </Button>
          )}
          {state === 'completed' && googleDocUrl && (
            <Anchor
              href={googleDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              data-testid="google-docs-link"
            >
              <Group gap={4} wrap="nowrap">
                {documentTitle ?? 'Open document'}
                <IconExternalLink size={14} />
              </Group>
            </Anchor>
          )}
          {state === 'failed' && exportId && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => retryExport()}
              loading={exportLoading}
              data-testid="google-docs-retry"
            >
              Retry
            </Button>
          )}
        </Group>
      </Group>

      {/* The reason matters more than the status: an export fails for a
          missing credential as readily as for a malformed report, and those
          need very different responses. */}
      {(state === 'failed' || state === 'not_configured') && error && (
        <Alert color="red" variant="light" mt="xs" p="xs">
          <Text size="xs">{error}</Text>
        </Alert>
      )}
    </Paper>
  );
}

function ExportBadge({ state }: Readonly<{ state: string }>) {
  const badge: Record<string, { color: string; label: string }> = {
    completed: { color: 'green', label: 'Exported' },
    failed: { color: 'red', label: 'Export failed' },
    not_configured: { color: 'orange', label: 'Not configured' },
    polling: { color: 'blue', label: 'Exporting' },
    triggering: { color: 'blue', label: 'Starting' },
    checking: { color: 'gray', label: 'Checking' },
    idle: { color: 'gray', label: 'Not exported' },
  };
  const { color, label } = badge[state] ?? badge.idle;

  return (
    <Badge color={color} variant="light" size="sm" data-testid="google-docs-status">
      {label}
    </Badge>
  );
}
